import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Ticket as TicketIcon, PaperPlaneTilt, CheckSquare, Square } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";
import { PortalKeyDate, PortalApprovalStatus } from "@/components/PortalTicketReadOnly";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTicket() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    axios.get(`${API}/portal/t/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 410) {
          setError("This ticket link has expired. Please email support so they can send you a fresh one.");
        } else if (status === 429) {
          setError("This link has been requested too many times in a short window. Please try again in a few minutes.");
        } else {
          setError("This ticket link is invalid or has been revoked. Please contact support if you still need access.");
        }
      });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/portal/t/${token}/reply`, { body: reply });
      setReply("");
      load();
      toast.success("Reply sent to support");
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="grain min-h-screen w-full flex items-center justify-center p-6" style={{ background: "#FFFFFF" }}>
        <div className="hairline rounded-lg bg-white p-6 max-w-md text-center">
          <div className="text-sm text-slate-700">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-slate-500 text-sm" style={{ background: "#FFFFFF", minHeight: "100vh" }}>Loading…</div>;
  }
  const { ticket, comments } = data;

  return (
    <div className="grain min-h-screen p-6" style={{ background: "#FFFFFF" }}>
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90] flex items-center justify-center">
            <TicketIcon size={20} weight="duotone" color="white" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">Aurora Business Systems Support</div>
            <div className="text-[11px] text-slate-500 font-mono">your ticket</div>
          </div>
        </div>

        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-500">#{ticket.number}</span>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{ticket.subject}</h1>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`chip status-${ticket.status}`}>{ticket.status.replace("_", " ")}</span>
            <span className={`chip prio-${ticket.priority}`}>{ticket.priority}</span>
            <span className="text-xs text-slate-500 font-mono">Updated {timeAgo(ticket.updated_at)}</span>
          </div>
        </div>

        <div className="hairline rounded-lg bg-white p-4">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Your original request</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap mt-2">{ticket.description}</div>
        </div>

        <PortalKeyDate ticket={ticket} />
        <PortalApprovalStatus ticket={ticket} />

        {Array.isArray(ticket.checklist_items) && ticket.checklist_items.length > 0 && (
          <div className="hairline rounded-lg bg-white p-4" data-testid="portal-checklist">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Progress checklist</div>
              <span className="text-[11px] font-mono text-slate-500">
                {ticket.checklist_items.filter((i) => i.is_checked).length}/{ticket.checklist_items.length} complete
              </span>
            </div>
            <ul className="space-y-1.5">
              {[...ticket.checklist_items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item) => (
                <li
                  key={item.id}
                  className="flex items-start gap-2 text-sm text-slate-800"
                  data-testid={`portal-checklist-item-${item.id}`}
                >
                  {item.is_checked ? (
                    <CheckSquare size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                  ) : (
                    <Square size={14} weight="regular" className="text-slate-400 mt-0.5 shrink-0" />
                  )}
                  <span className={item.is_checked ? "line-through text-slate-500" : ""}>{item.label}</span>
                </li>
              ))}
            </ul>
            <div className="text-[10px] text-slate-500 font-mono mt-2 italic">
              This checklist is maintained by the support team.
            </div>
          </div>
        )}

        {comments.map((c) => (
          <div key={c.id} className="hairline rounded-lg bg-white p-4">
            <div className="text-sm font-medium">{c.author_name}</div>
            <div className="text-[11px] text-slate-500 font-mono">{timeAgo(c.created_at)}</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap mt-2">{c.body}</div>
          </div>
        ))}

        <div className="hairline rounded-lg bg-white p-4">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mb-2">Add a reply</div>
          <textarea
            data-testid="public-reply-box"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply — it will be added to the ticket and the team will see it."
            className="w-full bg-[#FFFFFF] border border-slate-200 rounded-md p-2 text-sm min-h-24 outline-none focus:ring-1 focus:ring-[#4f4c90]"
          />
          <div className="flex justify-end mt-2">
            <Button
              data-testid="public-reply-submit"
              onClick={send}
              disabled={sending || !reply.trim()}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1"
            >
              <PaperPlaneTilt size={14} /> {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>

        <div className="text-[11px] text-slate-500 text-center font-mono pt-4">
          You can also reply to any email from support.bsys@auroraenergy.co to update this ticket.
        </div>
      </div>
    </div>
  );
}
